import React, { useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  className,
  icon,
  dataTestId,
  disabled = false,
  preserveOrder = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Sort A-Z unless preserveOrder is true (for tree-structured data)
  const processedOptions = preserveOrder
    ? options
    : [...options].sort((a, b) =>
        (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
      );

  // Filter based on search (strip └ prefix for matching)
  const filteredOptions = search
    ? processedOptions.filter((opt) => {
        const cleanLabel = (opt.label || '').replace(/^\s*└─?\s*/, '').trim();
        return cleanLabel.toLowerCase().includes(search.toLowerCase());
      })
    : processedOptions;

  const selectedOption = processedOptions.find((opt) => opt.value === value);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'justify-between font-normal h-9 text-sm',
            !selectedOption && 'text-muted-foreground',
            className
          )}
          data-testid={dataTestId}
        >
          <span className="flex items-center gap-1.5 truncate text-left">
            {icon}
            <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
          </span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '360px' }}
        align="start"
      >
        <div className="flex flex-col">
          {/* Search row — borderless, just icon + text */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-sm outline-none border-none ring-0 shadow-none placeholder:text-slate-400 focus:outline-none focus:ring-0 focus:border-none"
              autoFocus
            />
          </div>

          {/* Options list */}
          <div className="max-h-[260px] overflow-y-auto overflow-x-hidden p-1">
            {filteredOptions.length === 0 ? (
              <div className="py-3 text-sm text-center text-slate-500">{emptyMessage}</div>
            ) : (
              filteredOptions.map((option) => {
                const isParent = preserveOrder && !(option.label || '').trimStart().startsWith('└');
                const isSelected = value === option.value;
                return (
                  <div
                    key={option.value}
                    onClick={() => {
                      onValueChange(option.value === value ? '' : option.value);
                      setOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none transition-colors',
                      isSelected
                        ? 'bg-gold-50 text-gold-900 font-medium'
                        : isParent
                          ? 'bg-amber-50/70 text-slate-800 font-semibold hover:bg-amber-100/80'
                          : 'hover:bg-slate-50 text-slate-600 pl-4'
                    )}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-3.5 w-3.5 shrink-0',
                        isSelected ? 'opacity-100 text-gold-600' : 'opacity-0'
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
